import {
	Dialog,
	ImageGallery,
	ImageViewer,
	Toolbar
} from "client/Components";
import { Key } from "client/Misc/commonTypes";
import { localize } from "client_legacy/ndaw/culture";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import styles from "./HubImageViewerDialog.module.less";
import { useOpenDialog } from "client/Hooks";
import { clamp } from "client/Misc/utils";
import { ImageUrlWithAttribution } from "./common";
import { ImageAttributionLink } from "./ImageAttributionLink";

type HubImageViewerDialogProps = {
	onClose: () => void;
	initialImageUrl: string;
	images: readonly ImageUrlWithAttribution[];
};

const HubImageViewerDialog = (props: HubImageViewerDialogProps): ReactElement => {
	const {
		onClose,
		initialImageUrl,
		images
	} = props;

	if (images.length === 0) {
		throw new Error("No images were provided");
	}

	const [currentImageIndex, setCurrentImageIndex] = useState<number>(
		(): number => {
			const index: number = images.findIndex(x => x.url === initialImageUrl);
			return index === -1 ? 0 : index;
		}
	);

	const effectiveImageIndex: number = clamp(
		currentImageIndex,
		0,
		images.length - 1
	);

	const currentImage: ImageUrlWithAttribution = images[effectiveImageIndex];

	const nextHandler = useCallback(
		(): void => {
			setCurrentImageIndex((effectiveImageIndex + 1) % images.length);
		},
		[effectiveImageIndex, images.length]
	);

	const previousHandler = useCallback(
		(): void => {
			setCurrentImageIndex(
				(effectiveImageIndex + (images.length - 1)) % images.length
			);
		},
		[effectiveImageIndex, images.length]
	);

	useEffect(
		(): (() => void) => {
			const handleKeyDown = (event: KeyboardEvent): void => {
				const key: Key | null = tryResolveKeyboardKey(event);
				switch (key) {
					case Key.ArrowRight:
						nextHandler();
						break;
					case Key.ArrowLeft:
						previousHandler();
						break;
					default:
						// nothing to do
						break;
				};
			};

			window.addEventListener("keydown", handleKeyDown);

			return (): void => {
				window.removeEventListener("keydown", handleKeyDown);
			};
		},
		[nextHandler, previousHandler]
	);

	return (
		<Dialog
			trackingId="hub-image-viewer-dialog"
			size="large"
			isEnlargeable
		>
			<Dialog.Header
				title={localize("_ViewImage")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<div className={styles.content}>
					<div className={styles.imageViewerContainer}>
						<ImageViewer
							imageUrl={currentImage.url}
							trackingId="image-viewer"
						/>

						{currentImage.attribution && (
							<div className={styles.attributionContainer}>
								<ImageAttributionLink
									attribution={currentImage.attribution}
									trackingId="attribution"
								/>
							</div>
						)}
					</div>

					{images.length > 1 && (
						<ImageGallery
							activeIndex={effectiveImageIndex}
							thumbnailUrls={images.map(x => x.url)}
							onChangeActiveIndex={setCurrentImageIndex}
							trackingId="image-gallery"
						/>
					)}
				</div>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					{images.length > 1 && (
						<>
							<Toolbar.Button
								kind="primary"
								text={localize("_Previous")}
								onClick={previousHandler}
								trackingId="previous"
							/>

							<Toolbar.Button
								kind="primary"
								text={localize("_Next")}
								onClick={nextHandler}
								trackingId="next"
							/>
						</>
					)}

					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
HubImageViewerDialog.displayName = "HubImageViewerDialog";

export type HubImageViewerInitialData = Omit<
	HubImageViewerDialogProps,
	"onClose"
>;

export function useHubImageViewerDialog(): [
	element: ReactElement | null,
	open: (props: HubImageViewerInitialData) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<HubImageViewerDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
